import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router';
import axios from 'axios'
import VueAxios from 'vue-axios'
import moment from 'moment'
import tools from "@/composables/tools";
import { useHttpTimeout } from "@/composables/httpTimeout";


import { IonicVue } from '@ionic/vue';

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* Theme variables */
import './theme/variables.css';


import { currentUserStore } from '@/stores/currentUser'
import { User } from '@/interfaces/User';
import { recordRouteVisit } from '@/composables/visitedRoutes';

const pinia = createPinia()


const app = createApp(App)
  .use(IonicVue)
  .use(pinia)
  .use(VueAxios, axios)
  .use(router);

moment.locale('es');

app.config.globalProperties.$filters = {
  timeAgo(date: string) {
    return moment(date).fromNow()
  },
  human(date: string) {
    let val = moment(date).format('dddd D')
    val += ' de '
    val += moment(date).format('MMMM')
    return val
  },
}


router.isReady().then(() => {
  app.mount('#app');
});

const LOGIN_VIA_AUTH_GUARD = 'tecnogest_login_via_auth_guard'

router.beforeEach((to) => {
  if (to.meta.requiredAuth && localStorage.getItem('user') == undefined) {
    sessionStorage.setItem(LOGIN_VIA_AUTH_GUARD, '1')
    currentUserStore().$state.redirectAfterLogin = to.fullPath
    router.push({ path: "/login" });
  }
  return true
})

router.afterEach((to) => {
  recordRouteVisit(to)
})


axios.defaults.baseURL = process.env.VUE_APP_SERVICE_URL
axios.defaults.headers.common["Accept"] = "application/json"
axios.defaults.withCredentials = true
if (localStorage.getItem("token"))
  axios.defaults.headers.common["Authorization"] = "Bearer " + localStorage.getItem("token");

//hacemos un interceptor que derive a login cada vez que haya un error 401 no autenticado
axios.interceptors.response.use(
  response => response,
  error => {
    const isCanceledRequest =
      axios.isCancel?.(error) ||
      error?.code === 'ERR_CANCELED' ||
      String(error?.message || '').toLowerCase().includes('cancel');

    if (isCanceledRequest) {
      console.debug('ℹ️ Request cancelada (controlada):', error?.message || error?.code);
      return Promise.reject(error);
    }

    // Error de red (no hay respuesta del servidor)
    if (!error.response) {
      console.error('🔴 ERROR DE RED:', error.message);
      tools().toast('❌ Error de red: No se puede conectar con el servidor. Verifica tu conexión o que el backend esté iniciado.', 'danger', 15000)
      return Promise.reject(error);
    }

    // Error 401 - No autenticado
    if (error.response.status === 401) {
      console.log(error.response)
      tools().toast(error.response.data.message, 'danger')
      router.push({ path: "/login" });
    }
    // Otros errores con mensaje del servidor
    else if (error.response.data?.message) {
      tools().toast(error.response.data.message, 'danger', 10000)
    }
    // Error sin mensaje específico
    else {
      tools().toast(`Error del servidor (${error.response.status})`, 'danger', 10000)
    }
    return Promise.reject(error);
  });

//** guardamo como state los datos del usuario logeado */
const userJson = localStorage.getItem('user');
const currentUserObj: User = userJson !== null ? JSON.parse(userJson) : {}

// Validar que el usuario tenga los datos necesarios
// Si hay un token pero el usuario está vacío o sin datos válidos, cerrar sesión
const hasToken = localStorage.getItem('token');
if (hasToken && (!userJson || userJson === '{}' || !currentUserObj.id || !currentUserObj.permissions)) {
  console.error('❌ Usuario sin datos válidos o sin permisos. Cerrando sesión...');
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  window.location.href = '/login';
}

const currentUser = currentUserStore()
currentUser.update(currentUserObj)

// Monitor global para limpiar requests que llevan demasiado tiempo
const { cancelLongRunningRequests, getActiveRequests } = useHttpTimeout();

// Ejecutar limpieza cada 30 segundos
setInterval(() => {
  const activeRequests = getActiveRequests();

  // Si hay requests activas que llevan más de 25 segundos, cancelarlas
  if (activeRequests.length > 0) {
    console.log(`🔍 Monitoreando ${activeRequests.length} requests activas`);

    // Cancelar requests que llevan más de 25 segundos
    cancelLongRunningRequests(25000);

    // Log de requests que llevan mucho tiempo
    const longRunning = activeRequests.filter(req => req.duration > 15000);
    if (longRunning.length > 0) {
      console.warn(`⚠️ ${longRunning.length} requests llevan más de 15 segundos:`, longRunning);
    }
  }
}, 30000); // Cada 30 segundos

// También limpiar cuando la página se oculta o antes de cerrarla
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    console.log('🧹 Página oculta, limpiando requests...');
    cancelLongRunningRequests(5000); // Cancelar requests que llevan más de 5 segundos
  }
});

window.addEventListener('beforeunload', () => {
  console.log('🧹 Cerrando página, cancelando todas las requests...');
  cancelLongRunningRequests(0); // Cancelar todas las requests
});
